import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Camera, SpinnerGap, MapPin, Wrench, FacebookLogo, WhatsappLogo, PaperPlaneTilt, X, ImageSquare, ArrowsLeftRight, ListBullets, Images, CreditCard, Translate } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CITIES = ["Mamoudzou","Petite-Terre","Koungou","Majicavo","Sada","Combani","Dzoumogné","Bandrélé","Chiconi","Tsingoni","Ouangani","Chirongui","Bouéni","Kani-Kéli","Mtsamboro","Acoua","Bandraboua"];
const CONSTRUCTION_TYPES = ["Toiture","Climatisation","Portail","Peinture","Maçonnerie","Carrelage","Plomberie","Électricité","Menuiserie","Rénovation","Construction neuve","Terrassement","Clôture","Étanchéité"];

export default function PostGenerator({ onPostGenerated, usage, onSubscribe }) {
  const [description, setDescription] = useState('');
  const [city, setCity] = useState('Mamoudzou');
  const [constructionType, setConstructionType] = useState('Toiture');
  const [platform, setPlatform] = useState('facebook');
  const [language, setLanguage] = useState('fr');
  const [mode, setMode] = useState('standard');
  const [templateId, setTemplateId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const fileInputRef = useRef(null);
  const [imageAvant, setImageAvant] = useState(null);
  const [imageApres, setImageApres] = useState(null);
  const avantInputRef = useRef(null);
  const apresInputRef = useRef(null);

  useEffect(() => { axios.get(`${API}/templates`).then(res => setTemplates(res.data)).catch(err => console.error("Templates fetch error:", err.message)); }, []);

  const readFile = (file) => new Promise((resolve) => { const r = new FileReader(); r.onload = (e) => resolve(e.target.result); r.readAsDataURL(file); });
  const validateFile = (file) => {
    if (!['image/jpeg','image/png','image/webp'].includes(file.type)) { toast.error('Format: JPEG, PNG ou WEBP'); return false; }
    if (file.size > 10*1024*1024) { toast.error('Max 10 MB'); return false; }
    return true;
  };

  const handleMultiImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) { toast.error('Maximum 5 images'); return; }
    const valid = files.filter(validateFile);
    const newImgs = await Promise.all(valid.map(readFile));
    setImages(prev => [...prev, ...newImgs]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAvantApresUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file || !validateFile(file)) return;
    const data = await readFile(file);
    if (type === 'avant') setImageAvant(data); else setImageApres(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description && images.length === 0 && !imageAvant && !templateId) { toast.error('Ajoutez une description, une photo ou un template'); return; }
    if (mode === 'avant_apres' && (!imageAvant || !imageApres)) { toast.error('Ajoutez les photos Avant et Après'); return; }
    setLoading(true);
    try {
      const payload = { description, city, construction_type: constructionType, platform, mode, template_id: templateId, language };
      if (mode === 'avant_apres') { payload.image_avant_base64 = imageAvant; payload.image_apres_base64 = imageApres; }
      else { if (images.length === 1) payload.image_base64 = images[0]; else if (images.length > 1) payload.images_base64 = images; }
      const { data } = await axios.post(`${API}/posts/generate`, payload, { withCredentials: true });
      toast.success('Post généré !');
      if (onPostGenerated) onPostGenerated(data);
      setDescription(''); setImages([]); setImageAvant(null); setImageApres(null); setTemplateId(null);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur';
      toast.error(typeof msg === 'string' ? msg : 'Erreur serveur');
    } finally { setLoading(false); }
  };

  const canGenerate = usage?.can_generate !== false;
  const trialExpired = usage?.trial_expired === true;

  return (
    <div className="animate-fade-in-up" data-testid="post-generator">
      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Usage / Trial bar */}
        {usage && !usage.is_pro && (
          <div className="bg-[#8B5CF6]/5 border border-[#8B5CF6]/20 rounded-lg p-3" data-testid="usage-bar">
            {trialExpired ? (
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#A78BFA]">Période d'essai terminée</span>
                <button type="button" onClick={onSubscribe} className="text-xs font-bold text-[#8B5CF6] hover:underline flex items-center gap-1">
                  <CreditCard size={12} /> Passer Pro
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#A1A1AA]">
                  Essai : <strong className="text-[#F4F4F5]">{usage.posts_used}/{usage.posts_limit}</strong> posts utilisés
                </span>
                <div className="w-20 h-1.5 bg-[#27272A] rounded-full overflow-hidden">
                  <div className="h-full bg-[#8B5CF6] rounded-full transition-all" style={{ width: `${Math.min(100, (usage.posts_used / usage.posts_limit) * 100)}%` }} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Mode */}
        <div>
          <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#71717A] mb-2">Mode</label>
          <div className="flex gap-2">
            <button type="button" data-testid="mode-standard" onClick={() => setMode('standard')}
              className={`flex-1 h-10 rounded-lg border text-sm font-medium flex items-center justify-center gap-1.5 transition-all ${mode === 'standard' ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'bg-[#09090B] text-[#A1A1AA] border-[#27272A] hover:border-[#3F3F46]'}`}>
              <Images size={16} weight="duotone" /> Standard
            </button>
            <button type="button" data-testid="mode-avant-apres" onClick={() => setMode('avant_apres')}
              className={`flex-1 h-10 rounded-lg border text-sm font-medium flex items-center justify-center gap-1.5 transition-all ${mode === 'avant_apres' ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'bg-[#09090B] text-[#A1A1AA] border-[#27272A] hover:border-[#3F3F46]'}`}>
              <ArrowsLeftRight size={16} weight="duotone" /> Avant / Après
            </button>
          </div>
        </div>

        {/* Templates */}
        <div>
          <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#71717A] mb-2"><ListBullets size={14} className="inline mr-1" weight="duotone" />Templates</label>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {templates.map(t => (
              <button key={t.id} type="button" data-testid={`template-${t.id}`}
                onClick={() => setTemplateId(templateId === t.id ? null : t.id)}
                className={`shrink-0 h-9 px-3 text-xs rounded-lg border font-medium transition-all ${templateId === t.id ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'bg-[#09090B] text-[#A1A1AA] border-[#27272A] hover:border-[#3F3F46]'}`}>
                {t.name}
              </button>
            ))}
          </div>
        </div>

        {/* Platform */}
        <div>
          <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#71717A] mb-2">Plateforme</label>
          <div className="flex gap-2">
            <button type="button" data-testid="platform-facebook" onClick={() => setPlatform('facebook')}
              className={`flex-1 h-12 rounded-lg border font-semibold flex items-center justify-center gap-2 transition-all ${platform === 'facebook' ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'bg-[#09090B] text-[#A1A1AA] border-[#27272A] hover:border-[#3F3F46]'}`}>
              <FacebookLogo size={20} weight="duotone" /> Facebook
            </button>
            <button type="button" data-testid="platform-whatsapp" onClick={() => setPlatform('whatsapp')}
              className={`flex-1 h-12 rounded-lg border font-semibold flex items-center justify-center gap-2 transition-all ${platform === 'whatsapp' ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'bg-[#09090B] text-[#A1A1AA] border-[#27272A] hover:border-[#3F3F46]'}`}>
              <WhatsappLogo size={20} weight="duotone" /> WhatsApp
            </button>
          </div>
        </div>

        {/* City + Type */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#71717A] mb-2"><MapPin size={14} className="inline mr-1" weight="duotone" /> Ville</label>
            <select data-testid="city-select" value={city} onChange={(e) => setCity(e.target.value)}
              className="w-full h-12 px-3 border border-[#27272A] rounded-lg bg-[#09090B] text-[#F4F4F5] focus:border-[#8B5CF6] focus:ring-1 focus:ring-[#8B5CF6] outline-none text-sm">
              {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#71717A] mb-2"><Wrench size={14} className="inline mr-1" weight="duotone" /> Type</label>
            <select data-testid="construction-type-select" value={constructionType} onChange={(e) => setConstructionType(e.target.value)}
              className="w-full h-12 px-3 border border-[#27272A] rounded-lg bg-[#09090B] text-[#F4F4F5] focus:border-[#8B5CF6] focus:ring-1 focus:ring-[#8B5CF6] outline-none text-sm">
              {CONSTRUCTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        {/* Language */}
        <div>
          <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#71717A] mb-2">
            <Translate size={14} className="inline mr-1" weight="duotone" /> Langue du post
          </label>
          <div className="flex gap-2">
            {[
              { id: 'fr', label: 'Français' },
              { id: 'shimaore', label: 'Shimaore' },
              { id: 'bilingual', label: 'Bilingue' },
            ].map(l => (
              <button key={l.id} type="button" data-testid={`lang-${l.id}`} onClick={() => setLanguage(l.id)}
                className={`flex-1 h-10 rounded-lg border text-xs font-medium transition-all ${language === l.id ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'bg-[#09090B] text-[#A1A1AA] border-[#27272A] hover:border-[#3F3F46]'}`}>
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#71717A] mb-2">Description</label>
          <textarea data-testid="description-input" value={description} onChange={(e) => setDescription(e.target.value)}
            rows={3} className="w-full p-3 border border-[#27272A] rounded-lg bg-[#09090B] text-[#E4E4E7] focus:border-[#8B5CF6] focus:ring-1 focus:ring-[#8B5CF6] outline-none resize-none text-sm leading-relaxed placeholder:text-[#71717A]"
            placeholder="Décrivez les travaux réalisés..." />
        </div>

        {/* Images */}
        {mode === 'standard' ? (
          <div>
            <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#71717A] mb-2"><ImageSquare size={14} className="inline mr-1" weight="duotone" /> Photos ({images.length}/5)</label>
            {images.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-2 mb-2">
                {images.map((img, i) => (
                  <div key={i} className="relative shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-[#27272A]">
                    <img src={img} alt={`Photo ${i+1}`} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setImages(prev => prev.filter((_,j)=>j!==i))}
                      className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/80 text-white rounded-full flex items-center justify-center" data-testid={`remove-img-${i}`}>
                      <X size={10} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {images.length < 5 && (
              <div onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-[#27272A] hover:border-[#8B5CF6]/50 bg-[#09090B] rounded-lg p-6 text-center cursor-pointer transition-colors" data-testid="image-upload-zone">
                <Camera size={32} weight="duotone" className="mx-auto text-[#71717A] mb-1" />
                <p className="text-xs text-[#71717A] font-medium">Ajouter des photos (max 5)</p>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleMultiImageUpload} className="hidden" data-testid="image-file-input" />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#71717A] mb-2">AVANT</label>
              {imageAvant ? (
                <div className="relative rounded-lg overflow-hidden border border-[#27272A] h-32" data-testid="avant-preview">
                  <img src={imageAvant} alt="Avant" className="w-full h-full object-cover" />
                  <div className="absolute top-1 left-1 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded font-bold">AVANT</div>
                  <button type="button" onClick={() => setImageAvant(null)} className="absolute top-1 right-1 w-6 h-6 bg-black/80 text-white rounded-full flex items-center justify-center"><X size={12} weight="bold" /></button>
                </div>
              ) : (
                <div onClick={() => avantInputRef.current?.click()} className="border-2 border-dashed border-red-500/30 hover:border-red-500/60 bg-red-500/5 rounded-lg p-4 text-center cursor-pointer transition-colors h-32 flex flex-col items-center justify-center" data-testid="avant-upload-zone">
                  <Camera size={24} weight="duotone" className="text-red-400 mb-1" /><p className="text-[10px] text-red-400 font-medium">AVANT</p>
                </div>
              )}
              <input ref={avantInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleAvantApresUpload(e, 'avant')} className="hidden" />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#71717A] mb-2">APRÈS</label>
              {imageApres ? (
                <div className="relative rounded-lg overflow-hidden border border-[#27272A] h-32" data-testid="apres-preview">
                  <img src={imageApres} alt="Après" className="w-full h-full object-cover" />
                  <div className="absolute top-1 left-1 bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded font-bold">APRÈS</div>
                  <button type="button" onClick={() => setImageApres(null)} className="absolute top-1 right-1 w-6 h-6 bg-black/80 text-white rounded-full flex items-center justify-center"><X size={12} weight="bold" /></button>
                </div>
              ) : (
                <div onClick={() => apresInputRef.current?.click()} className="border-2 border-dashed border-green-500/30 hover:border-green-500/60 bg-green-500/5 rounded-lg p-4 text-center cursor-pointer transition-colors h-32 flex flex-col items-center justify-center" data-testid="apres-upload-zone">
                  <Camera size={24} weight="duotone" className="text-green-400 mb-1" /><p className="text-[10px] text-green-400 font-medium">APRÈS</p>
                </div>
              )}
              <input ref={apresInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleAvantApresUpload(e, 'apres')} className="hidden" />
            </div>
          </div>
        )}

        <button type="submit" disabled={loading || !canGenerate} data-testid="generate-post-button"
          className="w-full h-14 bg-[#8B5CF6] text-white font-bold text-base rounded-lg hover:bg-[#7C3AED] transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
          {loading ? <><SpinnerGap size={22} className="animate-spin" /> Génération en cours...</>
            : !canGenerate ? 'Essai terminé — Passez en Pro'
            : <><PaperPlaneTilt size={22} weight="duotone" /> Générer le post</>}
        </button>
      </form>
    </div>
  );
}
